import react from 'react';
import styled, { css } from 'styled-components';

import {
  bigStone,
  paradigmLightBlue,
} from '@paradigm/design-system/src/colors';
import {
  useTakerExecutedRfqInfo,
  useTakerOrderbookFetcher,
} from '@paradigm/features/src/unified-rfqs/ui/drfq/hooks';
import { GrfqTrade } from '@paradigm/features/src/unified-rfqs/entities/grfq/domain';
import { Nullable } from '@paradigm/utils/types';
import { Trade } from '@paradigm/features/src/unified-rfqs/entities/shared';
import { useMaybeTrade } from '@paradigm/features/src/unified-rfqs/contexts/unified-trades';
import { Trade as DrfqTrade } from '@paradigm/features/src/unified-rfqs/entities/drfq/trades';
import useFetchedDrfq from '@paradigm/features/src/unified-rfqs/contexts/use-fetched-drfq';

import VirtualizedRow from '#/components/rfq-common/TableRow';
import useIsMobile from '@paradigm/design-system/src/hooks/useIsMobile';
import BottomTableRow from '#/components/UnifiedRfqs/BottomTableRow';
import useColumns from '#/components/UnifiedRfqs/trade-history/columns';
import { useContextMenuActions } from '#/components/UnifiedRfqs/context-menu/context-menu';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import { RfqProvider } from '@paradigm/features/src/unified-rfqs/ui/rfq-context';
import { DESCRIPTION_COLUMN_WIDTH } from '#/components/UnifiedRfqs/components/strategy-description/StrategyDescription';
import { SpreadFromMarkColumnLabel } from '../components/bottom-tabs/SpreadFromMarkColumnLabel';
import { getTradeProductCode } from './columns';

interface Props {
  readonly tradeId: Trade['id'];
  readonly scrollableRef: react.RefObject<HTMLDivElement>;
}

export default function TradeRow(props: Props) {
  const { scrollableRef } = props;
  const { tradeId } = props;

  return (
    <VirtualizedRow
      id={tradeId}
      scrollableRef={scrollableRef}
      rowContent={() => <TradeRowContent tradeId={tradeId} />}
    />
  );
}

function RfqTradeRow({ trade }: { trade: Trade }) {
  if (trade.rfqType === 'drfq') {
    if (trade.role === 'MAKER') {
      return <DrfqMakerTradeRow trade={trade} />;
    }
    return <DrfqTakerTradeRow trade={trade} />;
  }
  return <GrfqTradeRow trade={trade} />;
}

function TradeRowContent({ tradeId }: { tradeId: Trade['id'] }) {
  const trade = useMaybeTrade(tradeId);
  if (trade == null) {
    return null;
  }

  return (
    <RfqProvider rfqId={trade.rfq_id}>
      <RfqTradeRow trade={trade} />
    </RfqProvider>
  );
}

function DrfqTakerTradeRow({ trade }: { trade: DrfqTrade }) {
  const { isLoading } = useTakerOrderbookFetcher(trade.rfq_id, {
    shouldFetch: trade.state === 'FILLED',
  });
  const { executedFromDesk: counterparty } = useTakerExecutedRfqInfo(
    trade.rfq_id,
    trade.side,
  );

  return (
    <BaseTradeRow
      trade={trade}
      counterparty={isLoading ? undefined : counterparty}
    />
  );
}

function DrfqMakerTradeRow({ trade }: { trade: DrfqTrade }) {
  const { isLoading, drfq } = useFetchedDrfq(trade.rfq_id);

  return (
    <BaseTradeRow
      trade={trade}
      counterparty={isLoading ? undefined : drfq?.taker_desk_name}
    />
  );
}

function GrfqTradeRow({ trade }: { trade: GrfqTrade }) {
  return <BaseTradeRow trade={trade} />;
}

function BaseTradeRow({
  trade,
  counterparty,
}: {
  trade: Trade;
  counterparty?: Nullable<string>;
}) {
  const account =
    trade.rfqType === 'grfq' ? trade.api_credential : trade.account;
  const productCode = getTradeProductCode(trade);
  const { open } = useContextMenuActions();
  const columns = useColumns({ trade, counterparty, account });

  return (
    <ResponsiveRow
      onContextMenu={({ x, y }) => {
        open({
          rfqId: trade.rfq_id,
          detailsItemId: trade.id,
          account,
          productCode,
          anchorPoint: { x, y },
        });
      }}
      dataTestId={`urfq-trade-history-${trade.id}`}
      columns={columns}
    />
  );
}

export function TradeHeaderRow() {
  const emptyColumns = useColumns();
  const isMobile = useIsMobile();
  return (
    <HeaderRow hidden={isMobile}>
      {emptyColumns.map(({ id, name }) => (
        <span key={id}>
          {name}
          {id === 'price' && <SpreadFromMarkColumnLabel />}
        </span>
      ))}
    </HeaderRow>
  );
}

const ROW_HEIGHT = 42;

const COLUMNS_CSS = css`
  grid-template-columns: 68px 50px 50px 190px ${DESCRIPTION_COLUMN_WIDTH}px 115px 100px 84px 50px 100px 80px 50px 50px 100px 160px 230px 230px 95px 200px 100px;
  grid-template-areas: 'date time auct prod  desc actions status counterparty qty price refPrice side  rol  rejp  rejr  tid  rfqid   currency account';
`;

const DesktopRow = styled.div`
  ${COLUMNS_CSS}
  display: grid;
  gap: 19px;
  align-items: center;
  padding: 0 22px;
  border-bottom: 1px solid ${bigStone};
  font-size: 14px;
  line-height: 16px;
  font-weight: 400;
  height: ${ROW_HEIGHT}px;
  color: #b5b9bd;
  background-color: #051524;
`;

const ResponsiveRow = styled(BottomTableRow)`
  ${COLUMNS_CSS}
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    .mobile-bottom-table-row {
      display: grid;
      grid-template-columns: 47px auto;
    }
  }
`;

const HeaderRow = styled(DesktopRow)`
  position: sticky;
  top: 0;
  z-index: 1;
  color: ${paradigmLightBlue};
`;
